import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  Grid2,
  Card,
  CardContent,
  Chip,
  IconButton,
} from "@mui/material";
import Header from "../Header";
import Footer from "../Footer";
import closeBtn from "../../assets/images/close.svg";
import rupee from "../../assets/images/rupee.svg";
import dateSmall from "../../assets/images/date-small.svg";
import userSmall from "../../assets/images/user-small.svg";
import Snackbars from "../../utils/Snackbars";
import useRequests from "../../utils/useRequests";
import * as Yup from "yup";
import { useFormik } from "formik";
import { format } from "date-fns";

const WalletBalance = () => {
  const [balance, setBalance] = useState("0.00");
  const [freezedBalance, setFreezedBalance] = useState("0.00");
  const [loading, setLoading] = useState(false);
  const [openWithdrawModal, setOpenWithdrawModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("balance");
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [savedUpiId, setSavedUpiId] = useState("");
  const [AlertData, setAlertData] = useState({
    variant: "",
    message: "",
  });
  const [copiedTxnId, setCopiedTxnId] = useState(null);

  const { getRequest, postRequest } = useRequests();

  const resetAlertData = () => {
    setTimeout(() => {
      setAlertData((AlertData) => ({
        ...AlertData,
        variant: "",
        message: "",
      }));
    }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
  };

  const fetchWalletBalance = async () => {
    setLoading(true);
    try {
      const response = await getRequest("user-wallet");
      if (response && response.code === 200) {
        if (response.data[0]) {
          setBalance(response.data[0].balance);
          setFreezedBalance(response.data[0].freezed_balance || "0.00");
          setSavedUpiId(response.data[0].upi_id);
        }
      } else {
        setAlertData({ 
          variant: "error", 
          message: "Failed to fetch wallet balance" 
        });
        resetAlertData();
      }
    } catch (error) {
      setAlertData({ 
        variant: "error", 
        message: "Error fetching wallet balance" 
      });
      resetAlertData();
    }
    setLoading(false);
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const response = await getRequest("withdrawal-history");
      if (response && response.code === 200) {
        setWithdrawalHistory(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching withdrawal history:", error);
    }
  };

  useEffect(() => {
    fetchWalletBalance();
    fetchWithdrawalHistory();
  }, []);

  const handleTabClick = (tab) => setSelectedTab(tab);

  const handleOpenWithdrawModal = () => {
    setOpenWithdrawModal(true);
    if (savedUpiId) {
      validation1.setFieldValue("upi_id", savedUpiId);
    }
  };

  const handleCloseWithdrawModal = () => {
    setOpenWithdrawModal(false);
    validation1.resetForm();
  };

  const handleOpenCancelModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setOpenCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setOpenCancelModal(false);
    setSelectedWithdrawal(null);
  };

  const handleCancelWithdrawal = async () => {
    if (!selectedWithdrawal) return;

    setCancelLoading(true);
    try {
      const jsonpar = JSON.stringify({
        uwt_id: selectedWithdrawal.uwt_id,
      });

      const res = await postRequest("cancel-withdrawal", jsonpar);
      
      if (res && res.code === 200) {
        setAlertData({
          variant: "success",
          message: "Withdrawal request cancelled successfully!",
        });
        resetAlertData();
        handleCloseCancelModal();
        fetchWalletBalance();
        fetchWithdrawalHistory();
      } else {
        setAlertData({
          variant: "error", 
          message: res.message || "Failed to cancel withdrawal request",
        });
        resetAlertData();
      }
    } catch (error) {
      console.error("Error cancelling withdrawal: ", error);
      setAlertData({
        variant: "error",
        message: "An error occurred while cancelling withdrawal",
      });
      resetAlertData();
    }
    setCancelLoading(false);
  };

  const validation1 = useFormik({
    enableReinitialize: true,
    initialValues: {
      amount: "",
      upi_id: savedUpiId || "",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .required("Amount is required")
        .min(1, "Amount must be at least ₹1")
        .max(parseFloat(balance) || 0, `Maximum withdrawal amount is ₹${balance}`)
        .typeError("Please enter a valid amount"),
      upi_id: Yup.string()
        .required("UPI ID is required")
        .matches(
          /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
          "Please enter a valid UPI ID (example: user@paytm)"
        ),
    }),
    onSubmit: async (values) => {
      try {
        let submit1 = document.getElementById("submit1");
        submit1.disabled = true;
        submit1.textContent = "Processing...";

        if (parseFloat(values.amount) > parseFloat(balance)) {
          setAlertData({
            variant: "error",
            message: "Insufficient balance",
          });
          resetAlertData();
          submit1.disabled = false;
          submit1.textContent = "Submit";
          return;
        }

        const jsonpar = JSON.stringify({
          amount: values.amount,
          upi_id: values.upi_id,
        });

        const res = await postRequest("wallet-withdrawal", jsonpar);
        
        if (res && res.code === 200) {
          setAlertData({
            variant: "success",
            message: "Withdrawal request submitted successfully!",
          });
          resetAlertData();
          handleCloseWithdrawModal();
          fetchWalletBalance();
          fetchWithdrawalHistory();
        } else {
          setAlertData({
            variant: "error", 
            message: res.message || "Failed to process withdrawal request",
          });
          resetAlertData();
        }
        
        submit1.disabled = false;
        submit1.textContent = "Submit";
      } catch (error) {
        console.error("Error: ", error);
        setAlertData({
          variant: "error",
          message: "An error occurred while processing withdrawal",
        });
        resetAlertData();
        
        let submit1 = document.getElementById("submit1");
        submit1.disabled = false;
        submit1.textContent = "Submit";
      }
    },
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#4CAF50';
      case 'pending':
        return '#FF9800';
      case 'cancelled':
      case 'rejected':
        return '#F44336';
      default:
        return '#757575';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd MMM yyyy, hh:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const canCancelWithdrawal = (withdrawal) => {
    const status = withdrawal.status?.toLowerCase();
    return status === 'pending';
  };

  const handleCopyTransactionId = (txnId) => {
    navigator.clipboard.writeText(txnId).then(() => {
      setCopiedTxnId(txnId);
      setTimeout(() => setCopiedTxnId(null), 2000);
    }).catch(() => {
      const textArea = document.createElement("textarea");
      textArea.value = txnId;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedTxnId(txnId);
      setTimeout(() => setCopiedTxnId(null), 2000);
    });
  };

    const styles = `
        @keyframes fadeInOut {
        0% { opacity: 0; transform: translateX(10px); }
        50% { opacity: 1; transform: translateX(0); }
        100% { opacity: 0; transform: translateX(10px); }
        }
    `;

    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.textContent = styles;
        document.head.appendChild(styleSheet);
    
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <div className="center-App" container direction="column">
        <Header name="My Wallet" />
        
        {AlertData.message !== "" ? (
          <Snackbars variant={AlertData.variant} message={AlertData.message} />
        ) : (
          ""
        )}

        <Box
          sx={{
            padding: "20px",
            float: "left",
            width: "100%",
            height: "calc(100vh - 160px)",
            overflow: "auto",
          }}
        >
          {/* Balance Card */}
          <Box sx={{
            background: "linear-gradient(135deg, #6638A2 0%, #8B5DBA 100%)",
            padding: "25px",
            borderRadius: "15px",
            marginBottom: "20px",
            color: "white",
            boxShadow: "0 4px 15px rgba(102, 56, 162, 0.3)",
          }}>
            {/* Available Balance */}
            <Box sx={{ textAlign: "center", marginBottom: "20px" }}>
              <Typography variant="h6" sx={{ 
                marginBottom: "10px",
                fontSize: "16px",
                fontWeight: "400",
                opacity: 0.9,
              }}>
                Available Balance
              </Typography>
              
              <Typography variant="h2" sx={{ 
                marginBottom: "10px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontSize: "2.2rem",
              }}>
                <img src={rupee} alt="₹" width={28} />
                {loading ? "Loading..." : Number(balance || 0).toFixed(2)}
              </Typography>
            </Box>

            {/* Requested Balance */}
            <Box sx={{ 
              textAlign: "center", 
              marginBottom: "20px",
              paddingTop: "15px",
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            }}>
              <Typography variant="h6" sx={{ 
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "400",
                opacity: 0.8,
              }}>
                Requested Balance
              </Typography>
              
              <Typography variant="h4" sx={{ 
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                fontSize: "1.5rem",
                opacity: 0.9,
              }}>
                <img src={rupee} alt="₹" width={22} />
                {loading ? "Loading..." : Number(freezedBalance || 0).toFixed(2)}
              </Typography>
              
              {parseFloat(freezedBalance) > 0 && (
                <Typography variant="body2" sx={{
                  fontSize: "12px",
                  opacity: 0.7,
                  marginTop: "5px",
                }}>
                  (Withdrawal requests in process)
                </Typography>
              )}
            </Box>

            {/* Total Balance */}
            <Box sx={{ 
              textAlign: "center",
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "10px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}>
              <Typography variant="body2" sx={{ 
                fontSize: "13px",
                fontWeight: "400",
                opacity: 0.9,
                marginBottom: "5px",
              }}>
                Total Wallet Balance
              </Typography>
              
              <Typography variant="h5" sx={{ 
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                fontSize: "1.3rem",
              }}>
                <img src={rupee} alt="₹" width={20} />
                {loading ? "Loading..." : (parseFloat(balance) + parseFloat(freezedBalance)).toFixed(2)}
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: "center" }}>
              <Button
                className="btn-common"
                variant="contained"
                onClick={handleOpenWithdrawModal}
                disabled={loading || parseFloat(balance) <= 0}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "25px",
                  padding: "12px 35px",
                  fontSize: "16px",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                }}
              >
                Withdraw Balance
              </Button>
              
              {parseFloat(balance) <= 0 && (
                <Typography sx={{
                  marginTop: "8px",
                  fontSize: "12px",
                  opacity: 0.7,
                }}>
                  No available balance to withdraw
                </Typography>
              )}
            </Box>
          </Box>

          {/* Tabs */}
          <Grid2 className="ticketType" sx={{ marginBottom: "15px" }}>
            <Grid2
              className={`ticketName ${selectedTab === "balance" ? "selected" : ""}`}
              onClick={() => handleTabClick("balance")}
              sx={{
                cursor: "pointer",
                padding: "10px 20px",
                backgroundColor: selectedTab === "balance" ? "#6638A2" : "#f5f5f5",
                color: selectedTab === "balance" ? "white" : "#666",
                borderRadius: "8px",
                textAlign: "center",
                fontWeight: "500",
                marginRight: "10px",
              }}
            >
              Balance Info
            </Grid2>
            <Grid2
              className={`ticketName ${selectedTab === "history" ? "selected" : ""}`}
              onClick={() => handleTabClick("history")}
              sx={{
                cursor: "pointer",
                padding: "10px 20px",
                backgroundColor: selectedTab === "history" ? "#6638A2" : "#f5f5f5",
                color: selectedTab === "history" ? "white" : "#666",
                borderRadius: "8px",
                textAlign: "center",
                fontWeight: "500",
              }}
            >
              Withdrawal History
            </Grid2>
          </Grid2>

          {/* Tab Content */}
          <div className="viewShow">
            {selectedTab === "balance" && (
              <Box sx={{
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "10px",
              }}>
                <Typography variant="h6" sx={{
                  color: "#6638A2",
                  marginBottom: "15px",
                  fontWeight: "500",
                }}>
                  Wallet Information
                </Typography>
                
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="body2" sx={{
                    color: "#666",
                    lineHeight: 1.6,
                    display: "flex",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}>•</span>
                    <strong>Available Balance:</strong> Amount ready for withdrawal
                  </Typography>
                </Box>
                
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="body2" sx={{
                    color: "#666",
                    lineHeight: 1.6,
                    display: "flex",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}>•</span>
                    <strong>Requested Balance:</strong> Withdrawal requests in process
                  </Typography>
                </Box>
                
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="body2" sx={{
                    color: "#666",
                    lineHeight: 1.6,
                    display: "flex",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}>•</span>
                    Processing time: 1-3 business days
                  </Typography>
                </Box>
                
                <Box sx={{ marginBottom: "12px" }}>
                  <Typography variant="body2" sx={{
                    color: "#666",
                    lineHeight: 1.6,
                    display: "flex",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}>•</span>
                    Withdrawal requests are processed during business hours
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{
                    color: "#666",
                    lineHeight: 1.6,
                    display: "flex",
                    alignItems: "center",
                  }}>
                    <span style={{ fontWeight: "500", color: "#333", marginRight: "8px" }}>•</span>
                    Ensure your UPI ID is correct before withdrawal
                  </Typography>
                </Box>
              </Box>
            )}

            {selectedTab === "history" && (
              <Box>
                {withdrawalHistory.length > 0 ? (
                  withdrawalHistory.map((withdrawal, index) => (
                    <Card key={index} sx={{ 
                      marginBottom: "15px", 
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderRadius: "10px",
                      border: withdrawal.status?.toLowerCase() === 'rejected'
                        ? "1px solid #ffcdd2"
                        : "none",
                    }}>
                      <CardContent sx={{ padding: "16px" }}>
                        <Box sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}>
                          <Typography variant="h6" sx={{
                            color: "#6638A2",
                            fontSize: "18px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}>
                            <img src={rupee} alt="₹" width={18} />
                            {withdrawal.amount}
                          </Typography>
                          
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Chip
                              label={withdrawal.status || 'Pending'}
                              sx={{
                                backgroundColor: getStatusColor(withdrawal.status),
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '500',
                                height: '24px',
                              }}
                            />
                            
                            {canCancelWithdrawal(withdrawal) && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleOpenCancelModal(withdrawal)}
                                sx={{
                                  color: "#F44336",
                                  borderColor: "#F44336",
                                  fontSize: "10px",
                                  padding: "4px 8px",
                                  minWidth: "auto",
                                  height: "24px",
                                  "&:hover": {
                                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                                    borderColor: "#F44336",
                                  },
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </Box>
                        </Box>

                        <Typography variant="body2" sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                          color: "#666",
                        }}>
                          <img src={userSmall} alt="" className="small-svg" style={{ marginRight: "8px" }} />
                          UPI: {withdrawal.upi_id}
                        </Typography>

                        <Typography variant="body2" sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                          color: "#666",
                        }}>
                          <img src={dateSmall} alt="" className="small-svg" style={{ marginRight: "8px" }} />
                          {formatDate(withdrawal.created_at)}
                        </Typography>

                        {withdrawal.transaction_id && withdrawal.status?.toLowerCase() === 'completed' && (
                          <Box sx={{
                            marginTop: "12px",
                            padding: "12px 14px",
                            backgroundColor: withdrawal.status?.toLowerCase() === 'completed' 
                              ? "#f0f8f0" 
                              : "#f8f9fa",
                            borderRadius: "8px",
                            border: withdrawal.status?.toLowerCase() === 'completed' 
                              ? "1px solid #c8e6c9" 
                              : "1px solid #e9ecef",
                            position: "relative",
                            overflow: "hidden",
                          }}>
                            {/* Status indicator line */}
                            <Box sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: "3px",
                              backgroundColor: withdrawal.status?.toLowerCase() === 'completed' 
                                ? "#4CAF50" 
                                : "#2196F3",
                            }} />
                            
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                                <Box sx={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "6px",
                                  backgroundColor: withdrawal.status?.toLowerCase() === 'completed' 
                                    ? "#e8f5e8" 
                                    : "#e3f2fd",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                }}>
                                  {withdrawal.status?.toLowerCase() === 'completed' ? '✅' : '🔗'}
                                </Box>
                                
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{
                                    fontSize: "10px",
                                    fontWeight: "700",
                                    color: "#666",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    marginBottom: "4px",
                                  }}>
                                    Transaction ID
                                  </Typography>
                                  <Typography variant="body2" sx={{
                                    color: "#333",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    fontFamily: "Monaco, 'Lucida Console', monospace",
                                    wordBreak: "break-all",
                                    lineHeight: 1.3,
                                    padding: "2px 0",
                                  }}>
                                    {withdrawal.transaction_id}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              {/* Enhanced Copy Button */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyTransactionId(withdrawal.transaction_id)}
                                  sx={{
                                    width: "32px",
                                    height: "32px",
                                    backgroundColor: copiedTxnId === withdrawal.transaction_id ? "#e8f5e8" : "#fff",
                                    border: copiedTxnId === withdrawal.transaction_id 
                                      ? "1px solid #4CAF50" 
                                      : "1px solid #ddd",
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: copiedTxnId === withdrawal.transaction_id ? "#e8f5e8" : "#f5f5f5",
                                      borderColor: copiedTxnId === withdrawal.transaction_id ? "#4CAF50" : "#bbb",
                                      transform: "translateY(-1px)",
                                    },
                                  }}
                                >
                                  <Typography sx={{
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}>
                                    {copiedTxnId === withdrawal.transaction_id ? '✓' : '📋'}
                                  </Typography>
                                </IconButton>
                                
                                {copiedTxnId === withdrawal.transaction_id && (
                                  <Typography sx={{
                                    fontSize: "10px",
                                    color: "#4CAF50",
                                    fontWeight: "600",
                                    animation: "fadeInOut 2s ease",
                                  }}>
                                    Copied!
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            
                            {withdrawal.status?.toLowerCase() === 'completed' && (
                              <Box sx={{
                                marginTop: "8px",
                                padding: "4px 8px",
                                backgroundColor: "rgba(76, 175, 80, 0.1)",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}>
                                <Typography sx={{
                                  fontSize: "10px",
                                  color: "#2e7d32",
                                  fontWeight: "500",
                                }}>
                                  ✅ Payment processed successfully
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {withdrawal.reason && (withdrawal.status?.toLowerCase() === 'rejected' || withdrawal.status?.toLowerCase() === 'cancelled') && (
                          <Box sx={{
                            marginTop: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            backgroundColor: withdrawal.status?.toLowerCase() === 'rejected' || withdrawal.status?.toLowerCase() === 'cancelled'
                              ? "#ffebee"
                              : "#e8f5e8",
                            border: withdrawal.status?.toLowerCase() === 'rejected' || withdrawal.status?.toLowerCase() === 'cancelled'
                              ? "1px solid #ffcdd2"
                              : "1px solid #c8e6c9",
                            position: "relative",
                          }}>
                            <Box sx={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              fontSize: "16px",
                            }}>
                              {withdrawal.status?.toLowerCase() === 'rejected' && "❌"}
                              {withdrawal.status?.toLowerCase() === 'cancelled' && "🚫"}
                            </Box>

                            <Typography variant="body2" sx={{
                              fontSize: "11px",
                              fontWeight: "600",
                              marginBottom: "6px",
                              color: withdrawal.status?.toLowerCase() === 'rejected' || withdrawal.status?.toLowerCase() === 'cancelled'
                                ? "#c62828"
                                : "#2e7d32",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                            }}>
                              {withdrawal.status?.toLowerCase() === 'rejected' ? "❌ REJECTION REASON" : 
                               withdrawal.status?.toLowerCase() === 'cancelled' ? "🚫 CANCELLATION REASON" : 
                               "ℹ️ REASON"}
                            </Typography>
                            
                            <Typography variant="body2" sx={{
                              color: withdrawal.status?.toLowerCase() === 'rejected' || withdrawal.status?.toLowerCase() === 'cancelled'
                                ? "#d32f2f"
                                : "#1b5e20",
                              fontSize: "13px",
                              lineHeight: 1.5,
                              fontWeight: "500",
                              paddingRight: "24px",
                            }}>
                              {withdrawal.reason}
                            </Typography>
                            
                            {(withdrawal.status?.toLowerCase() === 'rejected') && (
                              <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "8px",
                                padding: "6px 8px",
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                                borderRadius: "4px",
                                border: "1px solid rgba(211, 47, 47, 0.2)",
                              }}>
                                <Typography sx={{
                                  fontSize: "11px",
                                  color: "#c62828",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}>
                                  <span>⚠️</span>
                                  This withdrawal request was not processed. Amount has been refunded to your wallet.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Box sx={{
                    textAlign: "center",
                    padding: "40px 20px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "10px",
                  }}>
                    <Typography variant="h6" sx={{
                      color: "#666",
                      marginBottom: "8px",
                    }}>
                      No Withdrawal History
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: "#999",
                    }}>
                      You haven't made any withdrawals yet.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Box>
        
        <Footer />
      </div>

      {/* Withdrawal Modal */}
      <Modal
        open={openWithdrawModal}
        onClose={handleCloseWithdrawModal}
        className="modal-default"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "400px" },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            padding: "0",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box sx={{
            padding: "20px 24px 10px",
            borderBottom: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Typography variant="h6" component="h2">
              Withdraw Balance
            </Typography>
            <IconButton 
              onClick={handleCloseWithdrawModal} 
              size="small"
              sx={{ padding: "4px" }}
            >
              <img src={closeBtn} alt="Close" width={20} />
            </IconButton>
          </Box>

          <Box sx={{ padding: "20px 24px" }}>
            <Typography variant="body2" sx={{
              color: "#666",
              marginBottom: "20px",
              textAlign: "center",
              backgroundColor: "#f0f8ff",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #e3f2fd",
            }}>
              Available Balance: <strong style={{ color: "#6638A2" }}>₹{Number(balance || 0).toFixed(2)}</strong>
              {parseFloat(freezedBalance) > 0 && (
                <div style={{ fontSize: "11px", marginTop: "4px", color: "#999" }}>
                  Requested: ₹{Number(freezedBalance || 0).toFixed(2)} (In Process)
                </div>
              )}
            </Typography>

            <form className="loginForm">
              <div className="position-relative">
                <img
                  src={rupee}
                  alt="Amount"
                  width={24}
                  className="textboxLefticon rupee-icon-positioned"
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  placeholder="Enter amount to withdraw"
                  autoComplete="off"
                  type="number"
                  name="amount"
                  className={`form-control ${
                    validation1.touched.amount && validation1.errors.amount
                      ? "is-invalid"
                      : ""
                  }`}
                  value={validation1.values.amount || ""}
                  onChange={validation1.handleChange}
                  onBlur={validation1.handleBlur}
                  sx={{
                    "& input": {
                      paddingRight: "20px !important",
                    },
                  }}
                />
                {validation1.touched.amount && validation1.errors.amount ? (
                  <div className="invalid-feedback">
                    {validation1.errors.amount}
                  </div>
                ) : null}
              </div>

              <div className="position-relative">
                <Typography sx={{
                  position: "absolute",
                  left: "5px",
                  top: "33px",
                  zIndex: 1,
                  fontSize: "16px",
                  color: "#666",
                }}>
                  @
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  placeholder="Enter UPI ID (example: user@paytm)"
                  autoComplete="off"
                  type="text"
                  name="upi_id"
                  className={`form-control ${
                    validation1.touched.upi_id && validation1.errors.upi_id
                      ? "is-invalid"
                      : ""
                  }`}
                  value={validation1.values.upi_id || ""}
                  onChange={validation1.handleChange}
                  onBlur={validation1.handleBlur}
                  sx={{
                    "& input": {
                      paddingRight: "20px !important",
                    },
                  }}
                />
                {validation1.touched.upi_id && validation1.errors.upi_id ? (
                  <div className="invalid-feedback">
                    {validation1.errors.upi_id}
                  </div>
                ) : null}
              </div>

              <Typography variant="body2" sx={{
                color: "#666",
                fontSize: "12px",
                marginTop: "10px",
                marginBottom: "20px",
                backgroundColor: "#fff3cd",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ffeaa7",
              }}>
                <strong>Note:</strong> Please ensure your UPI ID is correct. Withdrawals cannot be reversed once processed.
              </Typography>

              <Grid2 className="button-bottom fullWidth" fullWidth sx={{ marginTop: "20px" }}>
                <Button
                  className="btn-common"
                  variant="contained"
                  color="primary"
                  fullWidth
                  id="submit1"
                  onClick={(e) => {
                    e.preventDefault();
                    validation1.handleSubmit();
                    return false;
                  }}
                  sx={{ 
                    marginBottom: "10px !important",
                    padding: "12px",
                    fontSize: "16px",
                    fontWeight: "500",
                    maxWidth: "none !important",
                    borderRadius: "4px !important",
                  }}
                >
                  Submit
                </Button>
                
                <Button
                  className="btn-common-border"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleCloseWithdrawModal}
                  sx={{
                    padding: "12px",
                    fontSize: "16px",
                  }}
                >
                  Cancel
                </Button>
              </Grid2>
            </form>
          </Box>
        </Box>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        open={openCancelModal}
        onClose={handleCloseCancelModal}
        className="modal-default"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "380px" },
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            padding: "0",
          }}
        >
          <Box sx={{
            padding: "20px 24px 10px",
            borderBottom: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Typography variant="h6" component="h2" sx={{ color: "#F44336" }}>
              Cancel Withdrawal
            </Typography>
            <IconButton 
              onClick={handleCloseCancelModal} 
              size="small"
              sx={{ padding: "4px" }}
            >
              <img src={closeBtn} alt="Close" width={20} />
            </IconButton>
          </Box>

          <Box sx={{ padding: "20px 24px" }}>
            <Typography variant="body1" sx={{
              color: "#333",
              marginBottom: "15px",
              textAlign: "center",
              lineHeight: 1.5,
            }}>
              Are you sure you want to cancel this withdrawal request?
            </Typography>

            {selectedWithdrawal && (
              <Box sx={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #e3f2fd",
              }}>
                <Typography variant="body2" sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                  color: "#333",
                  fontWeight: "500",
                }}>
                  Amount: ₹{selectedWithdrawal.amount}
                </Typography>
                
                <Typography variant="body2" sx={{
                  color: "#666",
                  marginBottom: "6px",
                  fontSize: "13px",
                }}>
                  UPI: {selectedWithdrawal.upi_id}
                </Typography>
                
                <Typography variant="body2" sx={{
                  color: "#666",
                  fontSize: "13px",
                }}>
                  Date: {formatDate(selectedWithdrawal.created_at)}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{
              color: "#F44336",
              fontSize: "12px",
              marginBottom: "20px",
              backgroundColor: "#ffebee",
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ffcdd2",
              textAlign: "center",
            }}>
              <strong>Note:</strong> Once cancelled, the amount will be refunded to your available balance immediately.
            </Typography>

            <Grid2 className="button-bottom fullWidth" fullWidth sx={{ marginTop: "20px" }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCancelWithdrawal}
                disabled={cancelLoading}
                sx={{ 
                  marginBottom: "10px",
                  padding: "12px",
                  fontSize: "16px",
                  fontWeight: "500",
                  backgroundColor: "#F44336",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                }}
              >
                {cancelLoading ? "Cancelling..." : "Yes, Cancel Withdrawal"}
              </Button>
              
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCloseCancelModal}
                disabled={cancelLoading}
                sx={{
                  padding: "12px",
                  fontSize: "16px",
                  color: "#666",
                  borderColor: "#ddd",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#ddd",
                  },
                }}
              >
                Keep Withdrawal
              </Button>
            </Grid2>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default WalletBalance;